<template>
    <div :data-parameter-group="parameterGroupIdentifier" :data-parameter-key="parameter.key">
        <b-input v-if="parameter.inputType === 'text'" type="text" :disabled="!isEditable"
                 v-model="internalValue"/>

        <b-form-textarea v-else-if="parameter.inputType === 'text-area'" type="text"
                       rows="1" :disabled="!isEditable"
                       max-rows="32"
                       v-model="internalValue"/>

        <b-checkbox v-else-if="parameter.inputType === 'boolean_switch'" switch :disabled="!isEditable"
                    v-model="internalValue" v-bind="[$attrs]" :value="true" :unchecked-value="false"
        >
            {{ label }}
        </b-checkbox>

        <b-radio-group v-else-if="parameter.inputType === 'select_one'"
                       v-model="internalValue" :disabled="!isEditable"
                       :options="translateParameterOptions(parameter)"
                       stacked
        />

        <div v-else-if="parameter.inputType === 'select_and_sort_multiple'">
            <OmniCheckboxes v-if="isEditable"
                            :options="translateParameterOptionsForOmniCheckboxes(parameter)"
                            v-model="internalValue" draggable :grid="false"/>
            <OmniCheckboxes v-else :disabled="!isEditable"
                            :options="translateParameterOptionsForOmniCheckboxes(parameter)"
                            v-model="internalValue" />
        </div>

        <OmniCheckboxes v-else-if="parameter.inputType === 'select_multiple'" :disabled="!isEditable"
                        :options="translateParameterOptionsForOmniCheckboxes(parameter)"
                        v-model="internalValue" />

        <OmniDraggableList v-else-if="parameter.inputType === 'sort_values'" :disabled="!isEditable"
                           :options="translateParameterOptionsForOmniCheckboxes(parameter)"
                           v-model="internalValue" />

        <label-value-pairs-input v-else-if="parameter.inputType === 'label_value_pairs'" :disabled="!isEditable"
                                 :parameter="parameter"
                                 v-model="internalValue"
        />

        <theme-picker v-else-if="parameter.inputType === 'theme'" :disabled="!isEditable"
                      v-model="internalValue"
                      :options="parameter.options"/>

        <RefreshSystemSettingsCacheButton v-else-if="parameter.inputType === 'refresh_system_settings_cache'" :disabled="!isEditable"/>

        <RefreshTriggerCacheButton v-else-if="parameter.inputType === 'refresh_trigger_cache'"/>

        <unable-to-ship-reasons v-else-if="parameter.inputType === 'unable_to_ship_reasons'"
                                v-model="internalValue"
                                :parameter="parameter"
                                :disabled="!isEditable"
        />

        <i v-else class="text-danger">Onbekend input type "{{ parameter.inputType }}"</i>
    </div>
</template>

<script>
import LabelValuePairsInput from "@/components/ParameterCustomInputs/LabelValuePairs.vue";
import RefreshTriggerCacheButton from "@/components/ParameterCustomInputs/RefreshTriggerCacheButton.vue";
import UnableToShipReasons from "@/components/ParameterCustomInputs/UnableToShipReasons.vue";
import RefreshSystemSettingsCacheButton from "@/components/ParameterCustomInputs/RefreshSystemSettingsCacheButton.vue";

export default {
    name: 'ParameterInput',
    components: {RefreshSystemSettingsCacheButton, UnableToShipReasons, RefreshTriggerCacheButton: RefreshTriggerCacheButton, LabelValuePairsInput},
    
    props: {
        parameterGroupIdentifier: {required: true},
        parameter: {required: true},
        value: {required: true},
        label: {required: false},
        parameterLanguagePath: {required: false, default: `frontend-parameters.parameters`}
    },

    data() {
        return {
            internalValue: structuredClone(this.value),
        }
    },

    computed: {
        isEditable() {
            return this.hasAllPermissions([
                'appsettings.edit',
                ...(this.parameter.additionalRequiredEditPermissions ?? [])
            ])
        },
    },
    
    methods: {
        /**
         * Translates parameter options for general use.
         * 
         * @param {Object} parameter - The parameter object that includes the options to be translated.
         * @returns {Array} An array of objects containing the translated text and value for each option.
         */
        translateParameterOptions(parameter) {
            return parameter.options.map(option => {
                const translationIdentifier = `${this.parameterLanguagePath}.${this.parameterGroupIdentifier}.${parameter.key}.options.${option}`;
                let translation = this.$t(translationIdentifier)

                return {
                    html: translation,
                    text: translation,
                    value: option,
                };
            });
        },

        /**
         * Translates parameter options specifically for OmniCheckboxes, including a disabled flag.
         * 
         * @param {Object} parameter - The parameter object that includes the options to be translated.
         * @returns {Array} An array of objects containing the translated text, value, and disabled status for each option.
         */
        translateParameterOptionsForOmniCheckboxes(parameter) {
            return this.translateParameterOptions(parameter).map(option => ({
                ...option,
                disabled: false, // This is needed for OmniCheckboxes
            }));
        },
    },

    watch: {
        internalValue: {
            deep: true,
            handler() {
                this.$emit('input', this.internalValue)
            },
        }
    }
}
</script>
